import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileField } from "@/hooks/useHealthProfile";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import EmptyAfterDeleteDialog from "@/components/ui/molecules/EmptyAfterDeleteDialog";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { IconCircleX, IconEdit } from "@tabler/icons-react";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MedicalDetailType } from "@/types/health-profile";
import { useTranslation } from "react-i18next";
import { ISurgery } from "@/types/surgeries";

import { useNavigationSource } from "@/hooks/useNavigationSource";

const Surgeries: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const {
    fieldValue: surgeries = [],
    saveField: saveSurgeries,
    loading: surgeriesLoading,
    error: surgeriesError,
  } = useProfileField<ISurgery[]>(memberId, MedicalDetailType.SURGERIES, []);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [surgeryToDelete, setSurgeryToDelete] = useState<ISurgery | null>(null);

  if (surgeriesLoading) {
    return <MLoader />;
  }

  if (surgeriesError) {
    return (
      <MDialog
        isOpen={true}
        title={t("surgeries.couldNotLoad")}
        description={t("surgeries.couldNotLoadDescription")}
        primaryCtaText={t("common.retry")}
        onPrimaryCta={() => window.location.reload()}
        onClose={() => window.location.reload()}
      />
    );
  }

  const handleDelete = async () => {
    if (!surgeryToDelete) return;
    setDeleteLoading(true);
    try {
      const updatedSurgeries = surgeries.filter((s) => {
        if (surgeryToDelete.year && surgeryToDelete.month) {
          return !(
            s.name === surgeryToDelete.name &&
            s.year === surgeryToDelete.year &&
            s.month === surgeryToDelete.month
          );
        } else if (surgeryToDelete.date) {
          return !(
            s.name === surgeryToDelete.name && s.date === surgeryToDelete.date
          );
        }
        return true;
      });
      await saveSurgeries(updatedSurgeries);
      setShowDeleteModal(false);
      setSurgeryToDelete(null);
      if (updatedSurgeries.length === 0) {
        setShowEmptyDialog(true);
        return;
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (surgery: ISurgery, idx: number) => {
    navigate("/profile/health-profile/surgeries/SurgeriesForm", {
      state: {
        surgery,
        idx,
      },
    });
  };

  const getSecondaryText = (surgery: ISurgery) => {
    if (surgery.year && surgery.month) {
      const date = new Date(surgery.year, surgery.month - 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      return `${monthName} ${surgery.year}`;
    } else if (surgery.date) {
      const year = new Date(surgery.date).getFullYear();
      return year.toString();
    }
    return t("surgeries.yearNotAnswered");
  };

  const sortedSurgeries = [...surgeries].sort((a, b) => {
    const yearA = a.year || (a.date ? new Date(a.date).getFullYear() : 0);
    const yearB = b.year || (b.date ? new Date(b.date).getFullYear() : 0);
    if (yearA !== yearB) {
      return yearB - yearA;
    }
    const monthA = a.month || (a.date ? new Date(a.date).getMonth() + 1 : 0);
    const monthB = b.month || (b.date ? new Date(b.date).getMonth() + 1 : 0);
    return monthB - monthA;
  });

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Surgeries"
        memberInfo={memberInfo}
        onAdd={() =>
          navigate("/profile/health-profile/surgeries/SurgeriesForm?mode=add")
        }
        showGradient
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />
      <div className="profile-flex-col-gap">
        {surgeries.length > 0 &&
          sortedSurgeries.map((surgery, idx) => (
            <div
              key={`${surgery.name}-${surgery.year || surgery.date}-${
                surgery.month || ""
              }-${idx}`}
              style={{ position: "relative" }}
            >
              <ListItem
                usage="crud"
                type="2-line"
                variant="purple"
                title={surgery.name}
                secondaryText={getSecondaryText(surgery)}
                actions={[
                  {
                    id: "edit",
                    label: t("common.edit"),
                    icon: <IconEdit size={20} />,
                    onClick: () => handleEdit(surgery, idx),
                  },
                  {
                    id: "delete",
                    label: t("common.delete"),
                    icon: <IconCircleX size={20} />,
                    onClick: () => {
                      setSurgeryToDelete(surgery);
                      setShowDeleteModal(true);
                    },
                    className: "delete",
                  },
                ]}
              />
            </div>
          ))}
      </div>
      {showDeleteModal && surgeryToDelete && (
        <MDialog
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSurgeryToDelete(null);
          }}
          title={t("surgeries.deleteThisRecord")}
          description={t(
            `surgeries.deleteThisRecordDescription${
              memberInfo.onBehalf ? "_on_behalf" : ""
            }`
          )}
          primaryCtaText={t("common.delete")}
          onPrimaryCta={handleDelete}
          secondaryCtaText="Cancel"
          onSecondaryCta={() => {
            setShowDeleteModal(false);
            setSurgeryToDelete(null);
          }}
          isPrimaryCtaLoading={deleteLoading}
          isPrimaryCtaDisabled={deleteLoading}
        />
      )}
      <EmptyAfterDeleteDialog
        isOpen={showEmptyDialog}
        onConfirm={() => {
          setShowEmptyDialog(false);
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) navigate("/profile/health-profile");
        }}
      />
    </div>
  );
};

export default Surgeries;
